import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { IMAGES } from "@/data/content";

const ease = [0.22, 1, 0.36, 1];

export const Hero = () => {
  const { t } = useLang();
  const stats = [
    { n: "30+", l: t.hero.stat1 },
    { n: "200+", l: t.hero.stat2 },
    { n: "15+", l: t.hero.stat3 },
  ];

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden"
    >
      <div className="absolute inset-0">
        <img
          src={IMAGES.heroCar}
          alt="InovoDesign concept car"
          className="w-full h-full object-cover object-center scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050507] via-[#050507]/80 to-[#050507]/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050507] via-transparent to-[#050507]/60" />
      </div>

      <div className="relative z-10 mx-auto max-w-[1400px] w-full px-6 md:px-12 pt-32 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease }}
          className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 backdrop-blur px-4 py-1.5 mb-8"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-[#00F0FF] animate-pulse" />
          <span className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-300">
            {t.hero.badge}
          </span>
        </motion.div>

        <h1 className="max-w-4xl font-heading text-5xl sm:text-6xl lg:text-7xl tracking-tighter font-medium text-white leading-[1.02]">
          {[t.hero.titleA, t.hero.titleHighlight, t.hero.titleBPrefix].map((w, i) => (
            <motion.span
              key={i}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease, delay: 0.15 + i * 0.12 }}
              className="inline"
            >
              {i === 1 ? (
                <span className="text-[#00F0FF]">{w} </span>
              ) : i === 2 ? (
                <span>
                  {w}
                  <span className="text-[#00F0FF]">{t.hero.titleBAccent}</span>
                  {t.hero.titleBSuffix}{" "}
                </span>
              ) : (
                <span>{w} </span>
              )}
            </motion.span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease, delay: 0.55 }}
          className="mt-8 max-w-xl text-base sm:text-lg leading-relaxed text-zinc-400"
        >
          {t.hero.subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease, delay: 0.7 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href="#portfolio"
            data-testid="hero-cta-button"
            className="group inline-flex items-center gap-2 rounded-md bg-white px-6 py-3.5 text-sm font-medium text-black hover:bg-[#00F0FF] transition-colors duration-300"
          >
            {t.hero.ctaPrimary}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </a>
          <a
            href="#contact"
            data-testid="hero-secondary-button"
            className="inline-flex items-center gap-2 rounded-md border border-white/15 px-6 py-3.5 text-sm font-medium text-white hover:border-[#00F0FF]/50 hover:text-[#00F0FF] transition-colors duration-300"
          >
            {t.hero.ctaSecondary}
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="mt-20 grid grid-cols-3 gap-6 max-w-lg border-t border-white/10 pt-8"
        >
          {stats.map((s) => (
            <div key={s.l}>
              <div className="font-heading text-3xl font-medium text-white">
                {s.n}
              </div>
              <div className="mt-1 text-xs text-zinc-500 leading-tight">
                {s.l}
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
